/**
 * Overrides Report Page
 * Displays manual overrides and exceptions report
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  Tabs,
  Tab,
  IconButton,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, CopyableText } from '../../components/common';
import { OverridesService } from '../../services/reports/OverridesService';
import { formatDateTime } from '../../utils/dateUtils';
import type { OverrideData, KPICard, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  Select,
  SecondaryButton,
  DataTablePagination,
} from '../../components/ui';

type TabValue = 'mission-type' | 'geographic-area' | 'admin-user' | 'ghost' | 'asset';

const OverridesPage: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<OverrideData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('mission-type');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(15);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: 'Oct 5, 2023 - Nov 7, 2023',
    missionType: 'All Types',
    adminUser: 'All Users',
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await OverridesService.getOverridesData(filters, page + 1, rowsPerPage);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch overrides data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 14 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 14 }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getTrendColor = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const renderKPICard = (kpi: KPICard) => (
    <Card
      sx={{
        border: `1px solid ${theme.customColors.tableBorder}`,
        height: '100%',
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(1),
            fontWeight: 500,
          }}
        >
          {kpi.title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          {kpi.value}
        </Typography>
        {kpi.change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Box sx={{ color: getTrendColor(kpi.trend) }}>
              {getTrendIcon(kpi.trend)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: getTrendColor(kpi.trend),
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {kpi.change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const handleExportCSV = () => {
    if (!data) return;

    const headers = ['Timestamp', 'User', 'Reason', 'Mission Code', 'Category'];
    const rows = data.detailedOverrideLog.map((log) => [
      log.timestamp,
      log.user,
      log.reason,
      log.missionId,
      log.category || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `overrides-log-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const renderTableData = () => {
    if (!data) return null;

    switch (activeTab) {
      case 'mission-type':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>MISSION TYPE</TableHeaderCell>
                <TableHeaderCell>OVERRIDES</TableHeaderCell>
                <TableHeaderCell>OVERRIDE RATE</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.overridesByMissionType.map((item) => (
                <TableRow key={item.missionType}>
                  <TableCell>{item.missionType}</TableCell>
                  <TableCell>{item.overrides.toLocaleString()}</TableCell>
                  <TableCell>{item.overrideRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'geographic-area':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>GEOGRAPHIC AREA</TableHeaderCell>
                <TableHeaderCell>OVERRIDES</TableHeaderCell>
                <TableHeaderCell>OVERRIDE RATE</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.overridesByGeographicArea.map((item) => (
                <TableRow key={item.area}>
                  <TableCell>{item.area}</TableCell>
                  <TableCell>{item.overrides.toLocaleString()}</TableCell>
                  <TableCell>{item.overrideRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'admin-user':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>ADMIN USER</TableHeaderCell>
                <TableHeaderCell>OVERRIDES</TableHeaderCell>
                <TableHeaderCell>OVERRIDE RATE</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.overridesByAdminUser.map((item) => (
                <TableRow key={item.user}>
                  <TableCell>{item.user}</TableCell>
                  <TableCell>{item.overrides.toLocaleString()}</TableCell>
                  <TableCell>{item.overrideRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'ghost':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>GHOST</TableHeaderCell>
                <TableHeaderCell>OVERRIDES</TableHeaderCell>
                <TableHeaderCell>OVERRIDE RATE</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.overridesByGhost.map((item) => (
                <TableRow key={item.ghostId}>
                  <TableCell>{item.ghostName} ({item.ghostId})</TableCell>
                  <TableCell>{item.overrides.toLocaleString()}</TableCell>
                  <TableCell>{item.overrideRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'asset':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>ASSET</TableHeaderCell>
                <TableHeaderCell>OVERRIDES</TableHeaderCell>
                <TableHeaderCell>OVERRIDE RATE</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.overridesByAsset.map((item) => (
                <TableRow key={item.assetId}>
                  <TableCell>{item.assetName} ({item.assetId})</TableCell>
                  <TableCell>{item.overrides.toLocaleString()}</TableCell>
                  <TableCell>{item.overrideRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      default:
        return null;
    }
  };

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No data available.
        </Typography>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
          }}
        >
          Overrides Report
        </Typography>

        {/* Filters Section */}
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.customColors.tableBorder}`,
            marginBottom: theme.spacing(3),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: theme.spacing(2),
              cursor: 'pointer',
            }}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '1rem',
              }}
            >
              Filters
            </Typography>
            <IconButton size="small">
              {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={filtersOpen}>
            <Box
              sx={{
                padding: theme.spacing(2),
                paddingTop: 0,
                display: 'flex',
                gap: theme.spacing(2),
                flexWrap: 'wrap',
              }}
            >
              <Select
                size="small"
                label="Date Range"
                value={filters.dateRange || ''}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
                formControlProps={{ sx: { minWidth: 200 } }}
              >
                <MenuItem value="Oct 5, 2023 - Nov 7, 2023">Oct 5, 2023 - Nov 7, 2023</MenuItem>
                <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </Select>

              <Select
                size="small"
                label="Mission Type"
                value={filters.missionType || 'All Types'}
                onChange={(e) => setFilters({ ...filters, missionType: e.target.value as string })}
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All Types">All Types</MenuItem>
                <MenuItem value="Surveillance">Surveillance</MenuItem>
                <MenuItem value="Data Retrieval">Data Retrieval</MenuItem>
                <MenuItem value="Infiltration">Infiltration</MenuItem>
                <MenuItem value="Background Check">Background Check</MenuItem>
                <MenuItem value="Asset Search">Asset Search</MenuItem>
              </Select>

              <Select
                size="small"
                label="User"
                value={filters.adminUser || 'All Users'}
                onChange={(e) => setFilters({ ...filters, adminUser: e.target.value as string })}
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All Users">All Users</MenuItem>
                <MenuItem value="user_jane">user_jane</MenuItem>
                <MenuItem value="user_john">user_john</MenuItem>
                <MenuItem value="user_alex">user_alex</MenuItem>
                <MenuItem value="user_sarah">user_sarah</MenuItem>
                <MenuItem value="user_mike">user_mike</MenuItem>
              </Select>
            </Box>
          </Collapse>
        </Card>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} md={4}>
          {renderKPICard(data.totalOverrides)}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderKPICard(data.overrideRate)}
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              height: '100%',
            }}
          >
            <CardContent>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  marginBottom: theme.spacing(1),
                  fontWeight: 500,
                }}
              >
                Breakdown by Type
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                {data.breakdownByType.ai}% AI
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                {data.breakdownByType.human}% Human
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Override Volume Trend
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data.overrideVolumeTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name="Overrides"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Overrides by Category
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={data.overridesByCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(props: { value?: number; percent?: number }) => {
                      const entry = data.overridesByCategory.find((e) => e.value === props.value);
                      return entry ? `${entry.category}: ${((props.percent || 0) * 100).toFixed(0)}%` : '';
                    }}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {data.overridesByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                    formatter={(value: number) => value.toLocaleString()}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Overrides Table */}
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.customColors.tableBorder}`,
          marginBottom: theme.spacing(4),
        }}
      >
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: theme.customColors.tableBorder }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue as TabValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Tab label="By Mission Type" value="mission-type" />
              <Tab label="By Geographic Area" value="geographic-area" />
              <Tab label="By Admin User" value="admin-user" />
              <Tab label="By Ghost" value="ghost" />
              <Tab label="By Asset" value="asset" />
            </Tabs>
          </Box>
          <Box sx={{ marginTop: theme.spacing(2) }}>
            {renderTableData()}
          </Box>
          <DataTablePagination
            totalPages={Math.ceil(data.totalEntries / rowsPerPage)}
            page={page + 1}
            onPageChange={(_, newPage) => setPage(newPage - 1)}
            showStats
            startIndex={page * rowsPerPage + 1}
            endIndex={Math.min((page + 1) * rowsPerPage, data.totalEntries)}
            totalResults={data.totalEntries}
            layout="space-between"
          />
        </CardContent>
      </Card>

      {/* Bottom Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Reasons for Override
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={data.overrideReasons}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    type="number"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="reason"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    width={90}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                    formatter={(value: number) => value.toLocaleString()}
                  />
                  <Bar dataKey="count" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: theme.spacing(2),
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Detailed Override Log
                </Typography>
                <SecondaryButton
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportCSV}
                >
                  Export CSV
                </SecondaryButton>
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>TIMESTAMP</TableHeaderCell>
                    <TableHeaderCell>USER</TableHeaderCell>
                    <TableHeaderCell>REASON</TableHeaderCell>
                    <TableHeaderCell>MISSION CODE</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.detailedOverrideLog.slice(0, 10).map((log, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                        {formatDateTime(log.timestamp)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {log.user}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {log.reason}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        <CopyableText
                          text={log.missionId}
                          onTextClick={() => {
                            // Assuming missionId here is the one used in routing
                            window.open(`/missions/${log.missionId}`, '_blank');
                          }}
                          sx={{ color: theme.palette.primary.main }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainLayout>
  );
};

export default OverridesPage;

